import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, Info } from "lucide-react";

interface Template {
  id: string;
  name: string;
  visualization_type: string | null;
  template_text: string;
  description: string | null;
  is_active: boolean;
}

const LAYER_LABELS: Record<string, { label: string; description: string }> = {
  master: {
    label: "Master System Prompt",
    description: "Layer 1 — Universal voice rules, structure, TTS optimization, safety. Applied to every visualization.",
  },
  competition: {
    label: "Session: Competition",
    description: "Layer 2 — Modifier for competition visualizations. Defines objective, emotional intensity, and success criteria.",
  },
  practice: {
    label: "Session: Practice",
    description: "Layer 2 — Modifier for practice visualizations. Defines skill-building focus and session goals.",
  },
  recovery: {
    label: "Session: Recovery",
    description: "Layer 2 — Modifier for recovery visualizations. Defines healing focus and restoration goals.",
  },
};

function getTabKey(t: Template): string {
  return t.visualization_type === null ? "master" : t.visualization_type;
}

function getTabLabel(t: Template): string {
  const key = getTabKey(t);
  return LAYER_LABELS[key]?.label || t.name;
}

function getTabDescription(t: Template): string {
  const key = getTabKey(t);
  return LAYER_LABELS[key]?.description || t.description || "";
}

const AdminTemplates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Template | null>(null);
  const [saving, setSaving] = useState(false);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    const { data } = await supabase
      .from("prompt_templates")
      .select("*")
      .order("visualization_type", { nullsFirst: true });
    if (data) {
      const typed = data as Template[];
      // Sort: master (null type) first, then alphabetical
      const sorted = typed.sort((a, b) => {
        if (a.visualization_type === null) return -1;
        if (b.visualization_type === null) return 1;
        return (a.visualization_type || "").localeCompare(b.visualization_type || "");
      });
      setTemplates(sorted);
      if (sorted.length > 0 && !selected) {
        setSelected(sorted[0]);
        setEditText(sorted[0].template_text);
      }
    }
    setLoading(false);
  };

  const selectTemplate = (t: Template) => {
    setSelected(t);
    setEditText(t.template_text);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase
      .from("prompt_templates")
      .update({ template_text: editText, updated_at: new Date().toISOString() })
      .eq("id", selected.id);
    if (error) {
      toast.error("Failed to save template");
    } else {
      toast.success("Template saved");
      setSelected({ ...selected, template_text: editText });
      setTemplates((prev) =>
        prev.map((t) => (t.id === selected.id ? { ...t, template_text: editText } : t))
      );
    }
    setSaving(false);
  };

  // Extract {{variables}} from template text
  const extractVariables = (text: string) => {
    const matches = text.match(/\{\{([^}]+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, "")))];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Prompt Templates</h1>
          <p className="text-muted-foreground mt-1">
            Manage the 4-layer prompt system used by the AI visualization engine.
          </p>
        </div>

        {/* Template tabs */}
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTemplate(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selected?.id === t.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {getTabLabel(t)}
            </button>
          ))}
        </div>

        {selected && (
          <>
            {/* Layer description badge */}
            <div className="flex items-start gap-2 rounded-lg bg-secondary/30 border border-border/50 p-3">
              <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <p className="text-sm text-muted-foreground">
                {getTabDescription(selected)}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Editor */}
              <div className="lg:col-span-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-semibold">
                    {getTabLabel(selected)}
                  </h2>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full h-[600px] rounded-xl border border-border/50 bg-secondary/30 p-4 text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              {/* Variable sidebar */}
              <div className="space-y-4">
                <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Placeholders Used
                </h3>
                <div className="space-y-2">
                  {extractVariables(editText).map((v) => (
                    <div
                      key={v}
                      className="px-3 py-2 rounded-lg bg-secondary/50 text-sm font-mono text-foreground"
                    >
                      {`{{${v}}}`}
                    </div>
                  ))}
                  {extractVariables(editText).length === 0 && (
                    <p className="text-sm text-muted-foreground">No placeholders found.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTemplates;
